import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';

import Avatar from '@components/Avatar/Avatar';
import { EmailAvatar } from '@components/Avatar/EmailAvatar';
import { AddMemberType } from '@components/Invite/SelectMembersList';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import Icon from '@components/shared/Icon/Icon';
import { BaseButtonV3 } from '@components/TaskList/BaseButtonV3';
import { ProjectRoleSelectorWeb } from '@components/TaskList/ProjectRoleSelector.web';
import { SimpleHoverable } from '@components/TaskList/SimpleHoverable';
import {
  ChatDrawerScreen,
  useWebDrawer,
} from '@components/Web/Drawer/WebDrawerContext';
import { ProjectMemberRole, useCreateChatMutation } from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import { useAppNavigation } from '@navigation/useAppNavigation';

import Box from '../Box/Box';
import Text from '../Text/Text';

const ADD_SIZE = 32;
interface AddMembersSectionProps {
  members: AddMemberType[];
  currentUserTeamRole?: string;
  removeMember: (member: AddMemberType) => void;
  addMembers: () => void;
  updateMember?: (member: AddMemberType, role: string) => void;
  isCreateContactGroup?: boolean;
  isCreateTask?: boolean;
  isCreateProject?: boolean;
  isEditProject?: boolean;
  /** TA-10276: disables the "Add Members" trigger while the parent form is
   * submitting (part of the "lock the entire form" global spinner rule). */
  disabled?: boolean;
}

export const AddMembersSection: React.FC<AddMembersSectionProps> = ({
  members,
  currentUserTeamRole,
  removeMember,
  addMembers,
  updateMember,
  isCreateContactGroup,
  isCreateTask,
  isCreateProject,
  isEditProject,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { t: tModels } = useTranslation('models');
  const navigation = useAppNavigation();

  // Admin confirmation modal state
  const [showAdminConfirmModal, setShowAdminConfirmModal] = useState(false);
  const [pendingAdminChange, setPendingAdminChange] = useState<{
    member: AddMemberType;
    role: string;
  } | null>(null);
  const { chatListOpen, chatExpanded, setChatListOpen, setShouldChatListOpen } =
    useWebDrawer();
  const { setActiveMessageId, setIsOpenChat } = useActiveChat();

  const [createChat, { data: createChatData }] = useCreateChatMutation();

  useEffect(() => {
    if (!createChatData) return;
    const { createChat: chat } = createChatData;
    if (!chatExpanded && chatListOpen) {
      setShouldChatListOpen(true);
      setChatListOpen(false);
    }
    setIsOpenChat(true);
    setActiveMessageId?.(undefined);
    navigation.navigateToChatDrawer({
      screen: ChatDrawerScreen.details,
      activeChat: chat,
    });
  }, [createChatData]);

  const handleChatPress = (memberId: string) => {
    if (memberId.startsWith('invited-')) return;
    createChat({
      variables: {
        attributes: {
          userIds: [memberId],
        },
      },
    });
  };

  const showAddMemberLabel = members?.length > 0;
  const showAddMemberIcon = members?.length === 0;

  const getRoleLabel = (role: ProjectMemberRole): string => {
    if (role === ProjectMemberRole.Owner) return 'Owner';
    if (role === ProjectMemberRole.Admin) return 'Admin';
    if (role === ProjectMemberRole.Editor) return 'Editor';
    return 'Viewer';
  };

  const isOwnerRole = (role: string | undefined) =>
    role === ProjectMemberRole.Owner;

  const getDisplayName = (member: AddMemberType) => {
    const { name, firstName, lastName } = member;
    return name ?? `${firstName ?? ''} ${lastName ?? ''}`.trim();
  };

  const renderUser = (item: AddMemberType) => {
    const displayName = getDisplayName(item);
    const { avatar, email } = item;

    return (
      <Box
        flex={1}
        px='xs'
        py='m'
        mb='m'
        borderColor='grey02'
        borderWidth={1}
        borderRadius='xs'
        flexDirection='row'
        key={item.id}
        backgroundColor='white'
        alignItems='center'>
        {/* Left: render user / string item */}
        <Box flex={1} flexDirection='row' alignItems='center'>
          {item.id?.toString().startsWith('invited-') ? (
            <EmailAvatar width={40} icon='User' />
          ) : (
            <Avatar
              disableAvatarPress={true}
              size='medium'
              id={item.id}
              avatar={avatar}
              label={displayName}
              isMultiple={false}
            />
          )}
          <Box ml='xs'>
            <Text
              variant='webSmall'
              color='textPrimary'
              flexShrink={1}
              ellipsizeMode='tail'
              numberOfLines={1}>
              {displayName}
            </Text>
            {email && (
              <Text
                flexShrink={1}
                variant='webBodyHintSubline'
                color='grey04'
                marginTop='xxs'
                ellipsizeMode='tail'
                numberOfLines={1}>
                {email}
              </Text>
            )}
            {item.expiredAt && new Date(item.expiredAt) < new Date() && (
              <Text
                flexShrink={1}
                variant='webBodyHintSubline'
                color='alertRed'
                marginTop='xxs'
                numberOfLines={1}>
                {tModels('projects.members.inviteExpired', {
                  date: dayjs(item.expiredAt).format('MMM D, YYYY'),
                })}
              </Text>
            )}
          </Box>
        </Box>

        {isEditProject && !item.id?.toString().startsWith('invited-') && (
          <TooltipWebV3
            minWidth='auto'
            marginOffset={0}
            title={
              <Text
                numberOfLines={0}
                maxWidth={80}
                flexShrink={1}
                variant='labelSmall'
                color='white'>
                Message
              </Text>
            }
            placement='bottom-start'>
            <div style={{ flex: 1 }}>
              <SimpleHoverable
                onPress={() => {
                  handleChatPress(item.id);
                }}>
                {(hover) => (
                  <Box
                    width={32}
                    height={32}
                    borderRadius='xs'
                    backgroundColor={hover ? 'grey01' : 'white'}
                    alignItems='center'
                    justifyContent='center'>
                    <Icon
                      color='grey05'
                      variant='s'
                      name='MessageSquare'
                      marginHorizontal='none'
                    />
                  </Box>
                )}
              </SimpleHoverable>
            </div>
          </TooltipWebV3>
        )}
        {!(isEditProject && isOwnerRole(item.role)) && (
          <TooltipWebV3
            minWidth='auto'
            marginOffset={0}
            title={
              <Text
                numberOfLines={0}
                maxWidth={80}
                flexShrink={1}
                variant='labelSmall'
                color='white'>
                Remove
              </Text>
            }
            placement='bottom-start'>
            <div style={{ flex: 1 }}>
              <SimpleHoverable
                onPress={() => {
                  removeMember(item);
                }}>
                {(hover) => (
                  <Box
                    width={32}
                    height={32}
                    borderRadius='xs'
                    backgroundColor={hover ? 'grey01' : 'white'}
                    alignItems='center'
                    justifyContent='center'>
                    <Icon
                      color={hover ? 'alertRed' : 'grey05'}
                      variant='s'
                      name='Trash2'
                      marginHorizontal='none'
                    />
                  </Box>
                )}
              </SimpleHoverable>
            </div>
          </TooltipWebV3>
        )}

        {/* Right: role selector for User only */}
        {isEditProject && isOwnerRole(item.role) ? (
          <Box
            width={86}
            mx='m'
            alignItems='center'
            justifyContent='flex-end'
            flexDirection='row'>
            <Text variant='webSmall' color='textPrimary' marginRight='xs'>
              {getRoleLabel(item.role as ProjectMemberRole)}
            </Text>
          </Box>
        ) : (
          <BaseButtonV3
            noBorder={true}
            noRadius={false}
            noHover={false}
            buttonRadius='xs'
            modalRadius={8}
            showHoverWhenDropdown={true}
            popModalComponent={({ onClose }) => (
              <ProjectRoleSelectorWeb
                onRoleSelect={(role) => {
                  if (
                    isCreateTask ||
                    isCreateProject ||
                    (isEditProject && item.role !== ProjectMemberRole.Owner)
                  ) {
                    if (
                      isEditProject &&
                      role === ProjectMemberRole.Admin &&
                      item.role !== ProjectMemberRole.Admin
                    ) {
                      setPendingAdminChange({
                        member: item,
                        role,
                      });
                      setShowAdminConfirmModal(true);
                    } else {
                      updateMember?.(item, role);
                    }
                  }
                  onClose();
                }}
                selectedRole={item.role as ProjectMemberRole}
              />
            )}>
            <Box
              width={86}
              mx='m'
              alignItems='center'
              justifyContent='flex-end'
              flexDirection='row'>
              <Text variant='webSmall' color='textPrimary' marginRight='xs'>
                {getRoleLabel(item.role as ProjectMemberRole)}
              </Text>
              <Icon color='grey05' variant='s' name='ChevronDown' />
            </Box>
          </BaseButtonV3>
        )}
      </Box>
    );
  };

  return (
    <Box
      accessibilityLabel='members-list'
      flexDirection='column'
      justifyContent='center'
      marginBottom='s'
      marginTop='s'>
      <Box flex={1} flexDirection='row' justifyContent='space-between'>
        <Text variant='labelSmall' marginBottom='xs'>
          {t('models:projects.create.members.title')}
        </Text>
      </Box>
      {!(isCreateTask || isCreateProject || isEditProject) &&
        members.map((item) => {
          if (!item) return;
          const { name, avatar } = item;

          return (
            <Box flexDirection='column' key={item.id}>
              <Box flexDirection='row' alignItems='center' mb='xxs'>
                <Avatar size='medium' label={name} avatar={avatar} />
                <Box
                  flex={1}
                  flexDirection='row'
                  justifyContent='space-between'>
                  <Text
                    variant='labelSmall'
                    marginLeft='s'
                    textAlign='center'
                    numberOfLines={1}>
                    {name}
                  </Text>
                  <TouchableOpacity
                    accessibilityLabel={t('shared:remove')}
                    onPress={() => {
                      removeMember(item);
                    }}>
                    {isCreateContactGroup ? (
                      currentUserTeamRole && (
                        <Icon name='DeleteBin' color='black' />
                      )
                    ) : (
                      <Text
                        variant='labelSmall'
                        marginLeft='s'
                        textAlign='center'
                        numberOfLines={1}
                        color='alertRed'>
                        {t('shared:remove')}
                      </Text>
                    )}
                  </TouchableOpacity>
                </Box>
              </Box>
              <Box height={1} marginBottom='xxs' backgroundColor='grey02' />
            </Box>
          );
        })}
      {(isCreateTask || isCreateProject || isEditProject) &&
        members?.map((item) => renderUser(item))}

      {showAdminConfirmModal && (
        <ConfirmModal
          showModal={showAdminConfirmModal}
          isAlert={false}
          padding='l'
          onClose={() => {
            setShowAdminConfirmModal(false);
            setPendingAdminChange(null);
          }}
          onPress={() => {
            if (pendingAdminChange) {
              updateMember?.(
                pendingAdminChange.member,
                pendingAdminChange.role
              );
            }
            setShowAdminConfirmModal(false);
            setPendingAdminChange(null);
          }}
          buttonText={tModels('projects.members.confirmAdmin')}
          title={tModels('projects.members.changeToAdmin')}
          message={
            <Text>
              {tModels('projects.members.changeToAdminMessage', {
                name: pendingAdminChange
                  ? getDisplayName(pendingAdminChange.member)
                  : '',
              })}
            </Text>
          }
        />
      )}

      <SimpleHoverable
        onPress={addMembers}
        disabled={disabled}
        accessibilityLabel={t('shared:addMembers')}>
        {(hover) => (
          <Box
            backgroundColor={hover ? 'grey02' : 'grey01'}
            borderRadius='s'
            paddingStart='s'
            height={52}
            flexDirection='row'
            justifyContent='flex-start'
            alignItems='center'>
            {showAddMemberLabel && (
              <Box marginLeft='s' flexDirection='row' alignItems='center'>
                <Icon color='greenSecondary' variant='s' name='Plus'></Icon>
                <Text ml='s' mr='m' variant='labelSmall' color='greenSecondary'>
                  {t('shared:addMembers')}
                </Text>
              </Box>
            )}
            {showAddMemberIcon && (
              <Box justifyContent='center' alignItems='center'>
                <Box
                  flexDirection='row'
                  alignItems='center'
                  justifyContent='center'>
                  <Box
                    alignItems='center'
                    justifyContent='center'
                    borderRadius='xxl'
                    width={ADD_SIZE}
                    height={ADD_SIZE}>
                    <Icon
                      height={16}
                      width={16}
                      name='Plus'
                      color='greenSecondary'
                    />
                  </Box>
                  <Text
                    variant='labelSmall'
                    marginLeft='xs'
                    color='greenSecondary'>
                    {t('shared:addMembers')}
                  </Text>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </SimpleHoverable>
    </Box>
  );
};
